// src/component/Home.js
import React, { useEffect, useState, useRef } from "react";
import Typewriter from "typewriter-effect";
import { useNavigate, useLocation } from "react-router-dom";
import { useRecoilValue, useRecoilState } from "recoil";
import {
  completedStartScreenState,
  stateOfIntroductionState,
  userState,
} from "../atom/atoms";
import { getNavigationItems } from "../store/pouch/navigation-pouch";
import "../input.css";
import "./Home.css";
import RandomGreeting from "./start/RandomGreeting";
import HomeMenu from "./start/menu/HomeMenu";
import { addUserObj, getAllUsers, getUserObj } from "../store/pouch/user-pouch";
import { getQueryStringParams } from '../util/QueryStringUtil'; // Import the utility
import vxOAuthInstance from "../api/VxOAuthService";
import useUserInfoFromSurvey from '../hooks/useInfoFromSurveyVx'; // Import the custom hook
import useWeighHeightBMIFromSurveyWeighing, { calculateIdealWeight, calculateTargetBMIForTargetWeight } from '../hooks/useWeighHeightBMIFromSurveyWeighing'; // Import the custom hook
import PowerUp from './ideal/PowerUp';
import IdealWeight from './ideal/IdealWeight';
import CurrentWeight from "./ideal/CurrentWeight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"; // Ensure you have this import
import { faCrosshairs } from "@fortawesome/free-solid-svg-icons"; // Import the icon
import LifestyleBatteryIndicator from "./mission/sub/lifestyle-habit-history/LifestyleBatteryIndicator";
import { getHabitQuestionsByUserEmailAndDateTimeRange } from "../store/pouch/habit-pouch";
import { getLifestyleQuestionsByUserEmailAndDateTimeRange } from "../store/pouch/lifestyle-pouch";
import { getEndOfDayISOStringZ, getStartOfDayISOStringZ } from "../util/Util";

const Home = () => {
  const effectRan = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const completedStartScreen = useRecoilValue(completedStartScreenState);
  const stateOfIntroduction = useRecoilValue(stateOfIntroductionState);
  //apiVersion is useState, read process.env.REACT_APP_ENABLE_API_V1 for "v1" else "v0"
  const [apiVersion, setApiVersion] = useState(process.env.REACT_APP_ENABLE_API_V1 ? "v1" : "v0");
  // State to hold the email
  const [email, setEmail] = useState(null);
  const [latestWeight, setLatestWeight] = useState(0);
  const [targetWeight, setTargetWeight] = useState(0);
  const [targetBMI, setTargetBMI] = useState(0);
  const [idealWeight, setIdealWeight] = useState(0);
  const [needWeight, setNeedWeight] = useState(0);
  const { fetchUserInfo } = useUserInfoFromSurvey(email, setEmail, apiVersion); // Use the custom hook
  const { fetchUserWeightHeightBMI } = useWeighHeightBMIFromSurveyWeighing(email, setEmail, apiVersion);
  const [isCompletedSurvey, setIsCompletedSurvey] = useState(false);
  const [user, setUser] = useRecoilState(userState);
  const [lifestyleScore, setLifestyleScore] = useState(0);

  useEffect(() => {
    if (effectRan.current) return;
    effectRan.current = true;
    window.scrollTo(0, 0);
    const setLatestIdealWeight = async () => {
      
      
      let userWeightHeightInfo = await fetchUserWeightHeightBMI();
      
      setLatestWeight(userWeightHeightInfo?.latestWeight);
      const currentWeight = userWeightHeightInfo?.latestWeight;
      let needWeight = 0;
      let bmi = 0;

      // Determine the weight to use for calculations
      let tempIdeal = calculateIdealWeight(userWeightHeightInfo?.height);
      let tempTargetBMI = calculateTargetBMIForTargetWeight(userWeightHeightInfo?.height, user?.targetWeight) || 0;
      setTargetBMI(tempTargetBMI);
      let weightToUse = tempIdeal; // Default to ideal weight
      if (currentWeight < tempIdeal && currentWeight >= targetWeight) {
        weightToUse = targetWeight || tempIdeal; // Use target weight if exists
      } else if (currentWeight < targetWeight) {
        weightToUse = tempIdeal; // Use ideal weight if below target
      }

      debugger;
      setIdealWeight(tempIdeal);
      setTargetWeight(targetWeight);
      setNeedWeight(Math.round((tempIdeal - userWeightHeightInfo?.latestWeight) * 100) / 100);
    };

    const processNavigationData = async () => {
      try {
        const url = new URL(window.location.href);
        // Get query parameters
        const queryParams = getQueryStringParams();
        console.log("qp", queryParams); // Access the parameters as needed

        // Example: Accessing access_token
        const state = queryParams?.state?.replaceAll("/", "");
        // Check if state equals to localStorage frontEndState
        let userInfo = null;
        if (state && state === localStorage.getItem("frontEndState")) {
          userInfo = await fetchUserInfo();
          setIsCompletedSurvey(userInfo?.isCompletedSurvey);
          setUser(userInfo);
          
          await setLatestIdealWeight();
        } else if(user){
          setIsCompletedSurvey(user?.isCompletedSurvey);
          
          await setLatestIdealWeight();
        } else {
          console.log("state mismatch");
        }

        const usrObj = await getUserObj("active");
        let targetPath = null;

        //let debugUser = await getAllUsers();

        if (!completedStartScreen) {
          targetPath = '/start';
        } else if (!usrObj) {
          targetPath = '/signup-login';
        } else if (!(userInfo?.isCompletedSurvey || user?.isCompletedSurvey)) {
          targetPath = '/survey';
        }
                
        // Clear URL parameters
        const cleanUrl = `${window.location.origin}${window.location.pathname}${window.location.hash}`;
        window.history.replaceState({}, document.title, cleanUrl);

        // Navigate if necessary
        if (targetPath) {
          navigate(targetPath, { 
            replace: true,
            state: { clearSearch: true }
          });
        }

      } catch (error) {
        console.error("Error processing navigation items:", error);
      }
    };

    const fetchLifestyleScore = async () => {

      const lifestyleQuestions = await getLifestyleQuestionsByUserEmailAndDateTimeRange(
        user?.userEmail,
        getStartOfDayISOStringZ(),
        getEndOfDayISOStringZ()
      );
      const habitQuestions = await getHabitQuestionsByUserEmailAndDateTimeRange(
        user?.userEmail,
        getStartOfDayISOStringZ(),
        getEndOfDayISOStringZ()
      );
      const score = lifestyleQuestions.reduce((total, question) => {
        return total + (question.lifestyleQuestionResponse === "yes" ? 5 : 0);
      }, 0) + habitQuestions.reduce((total, question) => {
        return total + (question.answer === "no" ? 5 : 0);
      }, 0);

      setLifestyleScore(score);
    };
    
    fetchLifestyleScore();
    processNavigationData();
  }, [navigate, completedStartScreen, stateOfIntroduction, fetchUserInfo]);

  const handleNavigateWeighing = () => {
    
    navigate("/weighing");
  };

  const handleSetTarget = () => {
    
    navigate('/set-target');
  };

  const handleBatteryClick = () => {
    navigate('/fat-loss'); // Navigate to /fat-loss on click
  };

  return (
    <>
      <div className="flex flex-col h-screen">
        <>
          <div className="text-xl font-thin text-center text-blue-600 mb-4">
            <div>
              {
                <Typewriter
                  options={{
                    strings: [`Welcome to healthChat`],
                    autoStart: true,
                    loop: false,
                    deleteSpeed: Infinity,
                    pauseFor: 99999,
                    cursor: "",
                  }}
                />
              }
            </div>
          </div>

          <div className="chatbot-image-container2">
            <img
              src={`${process.env.PUBLIC_URL}/chat2/05-cute-chatbot.jpg`}
              alt="Chatbot"
              className="chatbot-image"
            />
          </div>
          <RandomGreeting />
          <div className="flex justify-center items-center"
          onClick={handleBatteryClick} 
          > 
            <LifestyleBatteryIndicator  lifestyleScore={lifestyleScore} 
            />
          </div>
          {isCompletedSurvey && ( 
            <div>
            <div className="flex justify-around mt-4 mx-2"
              onClick={handleNavigateWeighing}>
              <div className="w-[49%] mr-1">
                <IdealWeight weight={idealWeight} targetWeight={user?.targetWeight} targetBMI={targetBMI}/>
              </div>
              <div className="w-[49%] ml-1">
                <CurrentWeight need={needWeight} weight={latestWeight}/>
              </div>
            </div>
            <div className="mt-2">
              <button onClick={handleSetTarget}
              className="bg-blue-400 shadow-md shadow-blue-800/35 
                        min-w-[40vw] max-w-[80vw] py-4 rounded-lg flex flex-col items-center justify-center mx-auto" // Button styles
              >
                <FontAwesomeIcon icon={faCrosshairs} className="text-3xl mb-2 text-black" /> {/* Icon on top with margin-bottom */}
                <span className="text-lg text-white ">
                  {user?.targetWeight ? `Target: ${user.targetWeight} kg` : 'Set Target Weight'} {/* Conditional rendering */}
                </span>
              </button>
            </div>

            {/* <div className="flex justify-around mt-4">
              <div className="w-[49%] ml-1">
                <PowerUp />
              </div>
            </div> */}
          </div>)}
        </>
        <HomeMenu />
      </div>
    </>
  );
};

export default Home;
